import React, { useState } from "react";
import { Modal, Form, Button, Alert, Spinner } from "react-bootstrap";
import axios from "axios";

export default function AddPaymentModal({
    show,
    onHide,
    listerId,
    listerInfo,
    entityType = "lister",
    banks,
    t,
    onSuccess,
}) {
    const [submitting, setSubmitting] = useState(false);
    const [paymentErrors, setPaymentErrors] = useState(null);
    const [formData, setFormData] = useState({
        bank_id: "",
        amount: "",
        payment_date: new Date().toISOString().split("T")[0],
        notes: "",
    });

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const paymentTitle = {
        issuer: "Add Issuer Payment",
        lister: "Add Lister Payment",
        "market-placer": "Add Market Placer Payment",
        checker: "Add Checker Payment",
    }[entityType] || "Add Lister Payment";

    const handleAddPayment = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        setPaymentErrors(null);

        try {
            const token = localStorage.getItem("authToken");
            const payload = {
                bank_id: formData.bank_id ? parseInt(formData.bank_id) : null,
                amount: parseFloat(formData.amount),
                payment_date: formData.payment_date,
                notes: formData.notes,
            };

            const paymentPath = {
                issuer: "issuer",
                lister: "lister",
                "market-placer": "market-placer",
                checker: "checker",
            }[entityType] || "lister";

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/admin/${paymentPath}/add-payment/${listerId}`,
                payload,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            if (response.data?.success) {
                onSuccess(response.data.message || "Payment added successfully!");
                setFormData({
                    bank_id: "",
                    amount: "",
                    payment_date: new Date().toISOString().split("T")[0],
                    notes: "",
                });
                onHide();
            }
        } catch (err) {
            console.error("Error adding payment:", err);
            if (err.response?.status === 422) {
                setPaymentErrors(err.response?.data?.errors || { message: err.response?.data?.message });
            } else {
                setPaymentErrors({ message: err.response?.data?.message || "Server error occurred." });
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered className="rounded-4">
            <Modal.Header closeButton className="border-0 px-4 pt-4">
                <Modal.Title className="fw-bold">{t(paymentTitle)}</Modal.Title>
            </Modal.Header>
            <Form onSubmit={handleAddPayment}>
                <Modal.Body className="px-4">
                    {paymentErrors?.message && (
                        <Alert variant="danger" className="rounded-3">{paymentErrors.message}</Alert>
                    )}

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-secondary">{t("Payment Amount ($)")}</Form.Label>
                        <Form.Control
                            type="number"
                            step="0.01"
                            min="0.01"
                            name="amount"
                            value={formData.amount}
                            onChange={handleInputChange}
                            placeholder={`0`}
                            required
                            className="py-2 rounded-3"
                            isInvalid={!!paymentErrors?.amount}
                        />
                        <Form.Control.Feedback type="invalid">
                            {paymentErrors?.amount?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-secondary">{t("Payment Date")}</Form.Label>
                        <Form.Control
                            type="date"
                            name="payment_date"
                            value={formData.payment_date}
                            onChange={handleInputChange}
                            required
                            className="py-2 rounded-3"
                            isInvalid={!!paymentErrors?.payment_date}
                        />
                        <Form.Control.Feedback type="invalid">
                            {paymentErrors?.payment_date?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-secondary">{t("Select Bank")}</Form.Label>
                        <Form.Select
                            name="bank_id"
                            value={formData.bank_id}
                            onChange={handleInputChange}
                            className="py-2 rounded-3"
                            isInvalid={!!paymentErrors?.bank_id}
                        >
                            <option value="">{t("Select Bank (Optional)")}</option>
                            {banks.map((bank) => (
                                <option key={bank.id} value={bank.id}>
                                    {bank.name}
                                </option>
                            ))}
                        </Form.Select>
                        <Form.Control.Feedback type="invalid">
                            {paymentErrors?.bank_id?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                        <Form.Label className="fw-semibold text-secondary">{t("Notes")}</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={3}
                            name="notes"
                            value={formData.notes}
                            onChange={handleInputChange}
                            placeholder="Enter payment notes or reference ID..."
                            className="rounded-3"
                            isInvalid={!!paymentErrors?.notes}
                        />
                        <Form.Control.Feedback type="invalid">
                            {paymentErrors?.notes?.[0]}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Modal.Body>

                <Modal.Footer className="border-0 px-4 pb-4">
                    <Button variant="secondary" onClick={onHide} className="rounded-3 px-4 fw-semibold border-0">
                        {t("Cancel")}
                    </Button>
                    <Button variant="primary" type="submit" disabled={submitting} className="rounded-3 px-4 fw-semibold border-0">
                        {submitting ? <Spinner animation="border" size="sm" /> : t("Submit Payment")}
                    </Button>
                </Modal.Footer>
            </Form>
        </Modal>
    );
}